/**
 * useProjectSummary — THE SINGLE SOURCE OF TRUTH for project progress.
 *
 * This hook is the ONLY place where project progress is calculated.
 * Every component that shows project progress MUST use this hook.
 *
 * It queries Supabase directly (no stale module-level cache) and
 * listens for real-time updates via:
 *   1. Supabase Realtime (postgres_changes on schedule + checklist tables)
 *   2. Custom window event '__jphph_schedule_sync__' (from ScheduleTab)
 *   3. Visibility change (tab switch back)
 *
 * Progress formula (transparent):
 *   scheduleFinalProgress = completed schedule items / total items  (BINARY)
 *   checklistFinalProgress = final approved checklist items / total items
 *   projectProgress = (scheduleFinalProgress * 0.5) + (checklistFinalProgress * 0.5)
 *
 * A schedule item is "complete" if ANY of:
 *   - is_complete === true
 *   - progress >= 100
 *   - actual_end_date != null
 *   - ALL linked checklist items are fully approved
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { getMainStage, UNASSIGNED_STAGE } from '@/lib/scheduleStageMapping';
import { normalizeStoredProjectStage } from '@/lib/projectStageUpdate';
import { deduplicateChecklistItems } from '@/lib/checklistDedup';
import { jphDebugLog } from '@/lib/jphDebug';

export interface ProjectSummaryResult {
  // Schedule
  scheduleFinalProgress: number;
  scheduleFinalCompleted: number;
  scheduleFinalTotal: number;
  pendingScheduleItems: number;

  // Checklist
  checklistFinalProgress: number;
  checklistFinalCompleted: number;
  checklistFinalTotal: number;
  managementCompleted: number;
  managementTotal: number;
  subcontractorCompleted: number;
  subcontractorTotal: number;
  mgmtPct: number;
  subPct: number;
  subWaiting: boolean;

  // Combined
  projectProgress: number;

  // Stage
  activeStage: string;
  projectStatus: 'planning' | 'active' | 'complete' | 'on_hold';

  // Loading state
  loaded: boolean;
  loading: boolean;
}

const EMPTY: ProjectSummaryResult = {
  scheduleFinalProgress: 0,
  scheduleFinalCompleted: 0,
  scheduleFinalTotal: 0,
  pendingScheduleItems: 0,
  checklistFinalProgress: 0,
  checklistFinalCompleted: 0,
  checklistFinalTotal: 0,
  managementCompleted: 0,
  managementTotal: 0,
  subcontractorCompleted: 0,
  subcontractorTotal: 0,
  mgmtPct: 0,
  subPct: 0,
  subWaiting: false,
  projectProgress: 0,
  activeStage: 'Planning',
  projectStatus: 'planning',
  loaded: false,
  loading: true,
};

const STAGE_ORDER = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];

function isChecklistItemFinalComplete(item: {
  management_checked: boolean;
  requires_management_check?: boolean;
  subcontractor_checked: boolean;
  requires_subcontractor_check?: boolean;
}): boolean {
  const needsMgmt = item.requires_management_check !== false;
  const needsSub = item.requires_subcontractor_check === true;
  const mgmtOk = !needsMgmt || item.management_checked;
  const subOk = !needsSub || item.subcontractor_checked;
  return mgmtOk && subOk;
}

function isScheduleItemComplete(
  si: { is_complete: boolean; progress: number; actual_end_date?: string | null },
  linkedItems: Array<{ management_checked: boolean; requires_management_check?: boolean; subcontractor_checked: boolean; requires_subcontractor_check?: boolean }>
): boolean {
  if (si.is_complete) return true;
  if (si.progress >= 100) return true;
  if (si.actual_end_date != null && si.actual_end_date !== '') return true;
  if (linkedItems.length > 0 && linkedItems.every(isChecklistItemFinalComplete)) return true;
  return false;
}

export interface UseProjectSummaryOptions {
  /** When false, skip fetch and realtime until enabled. Default true. */
  enabled?: boolean;
}

export function useProjectSummary(
  projectId: string | undefined,
  options: UseProjectSummaryOptions = {}
): ProjectSummaryResult {
  const enabled = options.enabled !== false;
  const [result, setResult] = useState<ProjectSummaryResult>(EMPTY);
  const [tick, setTick] = useState(0);
  const loadingRef = useRef(false);
  // Track whether we've ever loaded data (for stale-while-revalidate)
  const hasLoadedOnce = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const debouncedRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => refresh(), 300);
  }, [refresh]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const compute = useCallback(async () => {
    if (!projectId || !enabled) return;
    if (loadingRef.current) return; // prevent concurrent fetches
    loadingRef.current = true;

    // Stale-while-revalidate: only set loading=true on first load
    // On subsequent refreshes, keep existing data visible
    setResult((prev) => ({ ...prev, loading: !hasLoadedOnce.current }));

    const isFirstLoad = !hasLoadedOnce.current;
    if (isFirstLoad) {
      jphDebugLog('[useProjectSummary] START first load', { projectId });
    }

    try {
      const [
        { data: scheduleRows, error: scheduleErr },
        { data: checklistRows, error: checklistErr },
        { data: projectRow },
      ] = await Promise.all([
        supabase
          .from('jphph_project_schedule_items')
          .select('id, is_complete, progress, actual_end_date, main_stage, phase, is_included, title, source_template_item_id')
          .eq('project_id', projectId)
          .eq('is_deleted', false),
        supabase
          .from('jphph_checklist_items')
          .select('id, management_checked, requires_management_check, subcontractor_checked, requires_subcontractor_check, source_template_item_id, title')
          .eq('project_id', projectId),
        supabase
          .from('jphph_projects')
          .select('current_stage, status')
          .eq('id', projectId)
          .maybeSingle(),
      ]);

      if (scheduleErr) {
        // eslint-disable-next-line no-console
        console.error('[useProjectSummary] Schedule fetch error:', scheduleErr.message);
      }

      if (checklistErr) {
        // eslint-disable-next-line no-console
        console.error('[useProjectSummary] Checklist fetch error:', checklistErr.message);
      }

      // CRITICAL FIX: Deduplicate checklist items using shared utility.
      // Handles both template-ID duplicates AND NULL-template-ID duplicates.
      const rawChecklist = checklistRows || [];
      const { unique: allChecklist, duplicates } = deduplicateChecklistItems(rawChecklist);

      // Build grouped stats for debug
      const groupedByRole = {
        management: { completed: 0, total: 0 },
        subcontractor: { completed: 0, total: 0 },
      };
      const groupedByChecklist: Record<string, number> = {};
      const duplicateTitles: string[] = [];
      for (const item of rawChecklist) {
        const clId = (item as { checklist_id?: string }).checklist_id || 'unknown';
        groupedByChecklist[clId] = (groupedByChecklist[clId] || 0) + 1;
        if (item.requires_management_check !== false) {
          groupedByRole.management.total += 1;
          if (item.management_checked) groupedByRole.management.completed += 1;
        }
        if (item.requires_subcontractor_check === true) {
          groupedByRole.subcontractor.total += 1;
          if (item.subcontractor_checked) groupedByRole.subcontractor.completed += 1;
        }
      }
      for (const d of duplicates) {
        if (!duplicateTitles.includes(d.title)) duplicateTitles.push(d.title);
      }

      // Management counts (from DEDUPED items only)
      const mgmtItems = allChecklist.filter((c) => c.requires_management_check !== false);
      const managementTotal = mgmtItems.length;
      const managementCompleted = mgmtItems.filter((c) => c.management_checked).length;
      const mgmtPct = managementTotal > 0 ? Math.round((managementCompleted / managementTotal) * 100) : 0;

      // Subcontractor counts (from DEDUPED items only)
      const subItems = allChecklist.filter((c) => c.requires_subcontractor_check === true);
      const subcontractorTotal = subItems.length;
      const subcontractorCompleted = subItems.filter((c) => c.subcontractor_checked).length;
      const subPct = subcontractorTotal > 0 ? Math.round((subcontractorCompleted / subcontractorTotal) * 100) : 0;
      const subWaiting = subcontractorTotal > 0 && mgmtPct === 100 && subPct < 100;

      // Checklist final counts (from DEDUPED items only)
      const checklistFinalTotal = allChecklist.length;
      const checklistFinalCompleted = allChecklist.filter(isChecklistItemFinalComplete).length;
      const checklistFinalProgress = checklistFinalTotal > 0
        ? Math.round((checklistFinalCompleted / checklistFinalTotal) * 100)
        : 0;

      jphDebugLog('Checklist aggregation root debug', {
        projectId,
        totalItems: rawChecklist.length,
        groupedByChecklist,
        groupedByRole,
        managementCompleted,
        managementTotal,
        subcontractorCompleted,
        subcontractorTotal,
        checklistFinalCompleted,
        checklistFinalTotal,
        checklistFinalProgress,
        uniqueItems: allChecklist.length,
        duplicateItems: {
          count: duplicates.length,
          titles: duplicateTitles,
          byTemplateId: duplicates.filter((d) => d.source_template_item_id).length,
          byNullTemplateId: duplicates.filter((d) => !d.source_template_item_id).length,
        },
      });

      jphDebugLog('Header checklist summary debug', {
        projectId,
        managementCompleted,
        managementTotal,
        subcontractorCompleted,
        subcontractorTotal,
        checklistFinalCompleted,
        checklistFinalTotal,
        checklistFinalProgress,
        rawChecklistItemsCount: rawChecklist.length,
      });

      // 3. Fetch mappings (schedule item -> checklist item)
      const scheduleIds = (scheduleRows || []).map((s) => s.id);
      let mappingRows: Array<{ project_schedule_item_id: string; project_checklist_item_id: string }> = [];

      if (scheduleIds.length > 0) {
        // Batch in chunks of 100 to avoid URL length limits
        const CHUNK = 100;
        for (let i = 0; i < scheduleIds.length; i += CHUNK) {
          const batch = scheduleIds.slice(i, i + CHUNK);
          const { data: mappings } = await supabase
            .from('jphph_checklist_schedule_item_mappings')
            .select('project_schedule_item_id, project_checklist_item_id')
            .in('project_schedule_item_id', batch);
          if (mappings) mappingRows.push(...(mappings as typeof mappingRows));
        }
      }

      // 4. Build linked checklist map: scheduleItemId -> checklist items
      const checklistById = new Map(
        (checklistRows || []).map((c) => [c.id, c])
      );
      const linkedMap = new Map<string, Array<typeof checklistRows[0]>>();
      for (const m of mappingRows) {
        if (!m.project_schedule_item_id || !m.project_checklist_item_id) continue;
        const ci = checklistById.get(m.project_checklist_item_id);
        if (!ci) continue;
        const arr = linkedMap.get(m.project_schedule_item_id) || [];
        arr.push(ci);
        linkedMap.set(m.project_schedule_item_id, arr);
      }

      // 5. Calculate schedule progress
      const includedSchedule = (scheduleRows || []).filter((s) => s.is_included !== false);
      const scheduleFinalTotal = includedSchedule.length;
      const scheduleFinalCompleted = includedSchedule.filter((si) => {
        const linked = linkedMap.get(si.id) || [];
        return isScheduleItemComplete(si, linked);
      }).length;
      const scheduleFinalProgress = scheduleFinalTotal > 0
        ? Math.round((scheduleFinalCompleted / scheduleFinalTotal) * 100)
        : 0;
      const pendingScheduleItems = scheduleFinalTotal - scheduleFinalCompleted;

      // 7. Combined project progress
      const projectProgress = Math.round(scheduleFinalProgress * 0.5 + checklistFinalProgress * 0.5);

      // 8. Suggested stage from schedule progress (fallback when current_stage not set)
      let suggestedStage = 'Handover';
      for (const stageName of STAGE_ORDER) {
        const stageItems = includedSchedule.filter((si) => {
          const stage = getMainStage(si);
          if (stage === UNASSIGNED_STAGE) return false;
          return stage === stageName;
        });
        if (stageItems.length === 0) continue;
        const allComplete = stageItems.every((si) => {
          const linked = linkedMap.get(si.id) || [];
          return isScheduleItemComplete(si, linked);
        });
        if (!allComplete) {
          suggestedStage = stageName;
          break;
        }
      }
      if (projectProgress === 100) suggestedStage = 'Completed';

      const storedStage = normalizeStoredProjectStage(
        (projectRow as { current_stage?: string | null } | null)?.current_stage
      );
      const activeStage = storedStage || suggestedStage;

      // 9. Project lifecycle status (active/planning/completed) — separate from workflow stage
      const dbStatus = ((projectRow as { status?: string | null } | null)?.status || '').toLowerCase();
      let projectStatus: ProjectSummaryResult['projectStatus'] = 'planning';
      if (dbStatus === 'completed' || dbStatus === 'complete' || projectProgress === 100) {
        projectStatus = 'complete';
      } else if (dbStatus === 'active') {
        projectStatus = 'active';
      } else if (dbStatus === 'planning') {
        projectStatus = 'planning';
      } else if (projectProgress > 0) {
        projectStatus = 'active';
      }

      const allStagesCompleted = scheduleFinalProgress === 100 && checklistFinalProgress === 100;
      if (allStagesCompleted) {
        projectStatus = 'complete';
      }

      const summary: ProjectSummaryResult = {
        scheduleFinalProgress,
        scheduleFinalCompleted,
        scheduleFinalTotal,
        pendingScheduleItems,
        checklistFinalProgress,
        checklistFinalCompleted,
        checklistFinalTotal,
        managementCompleted,
        managementTotal,
        subcontractorCompleted,
        subcontractorTotal,
        mgmtPct,
        subPct,
        subWaiting,
        projectProgress,
        activeStage,
        projectStatus,
        loaded: true,
        loading: false,
      };

      jphDebugLog('[useProjectSummary] Computed', {
        projectId,
        scheduleFinalProgress,
        checklistFinalProgress,
        projectProgress,
        scheduleFinalCompleted,
        scheduleFinalTotal,
        pendingScheduleItems,
        activeStage,
        allStagesCompleted,
        source: 'useProjectSummary',
      });

      setResult(summary);
      hasLoadedOnce.current = true;

      if (isFirstLoad) {
        jphDebugLog('[useProjectSummary] FIRST LOAD COMPLETE', {
          projectId,
          projectProgress,
          activeStage,
          scheduleFinalProgress,
          checklistFinalProgress,
          source: 'useProjectSummary',
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useProjectSummary] Error:', err);
      setResult((prev) => ({ ...prev, loading: false, loaded: true }));
    } finally {
      loadingRef.current = false;
    }
  }, [projectId, enabled]);

  // Initial load + re-run when tick changes — defer one frame so project header paints first.
  useEffect(() => {
    if (!enabled || !projectId) {
      setResult((prev) => ({
        ...prev,
        loaded: !projectId,
        loading: false,
      }));
      return;
    }
    const frame = requestAnimationFrame(() => {
      compute();
    });
    return () => cancelAnimationFrame(frame);
  }, [compute, tick, enabled, projectId]);

  // Listen for manual stage changes from Stages tab / project detail
  useEffect(() => {
    if (!projectId || !enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string; newStage: string }>).detail;
      if (detail?.projectId !== projectId) return;
      debouncedRefresh();
    };
    window.addEventListener('__jphph_stage_changed__', handler);
    return () => window.removeEventListener('__jphph_stage_changed__', handler);
  }, [projectId, debouncedRefresh, enabled]);

  // Listen for schedule sync events from ScheduleTab
  useEffect(() => {
    if (!projectId || !enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId: string }>).detail;
      if (detail?.projectId !== projectId) return;
      debouncedRefresh();
    };
    window.addEventListener('__jphph_schedule_sync__', handler);
    return () => window.removeEventListener('__jphph_schedule_sync__', handler);
  }, [projectId, debouncedRefresh, enabled]);

  // Supabase Realtime: schedule items
  useEffect(() => {
    if (!projectId || !enabled) return;
    const channel = supabase
      .channel(`project-summary-schedule-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'jphph_project_schedule_items', filter: `project_id=eq.${projectId}` },
        () => debouncedRefresh()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [projectId, debouncedRefresh, enabled]);

  // Supabase Realtime: checklist items
  useEffect(() => {
    if (!projectId || !enabled) return;
    const channel = supabase
      .channel(`project-summary-checklist-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'jphph_checklist_items', filter: `project_id=eq.${projectId}` },
        () => debouncedRefresh()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [projectId, debouncedRefresh, enabled]);

  // Refresh when page becomes visible again
  useEffect(() => {
    const handler = () => {
      if (!document.hidden && projectId && enabled) debouncedRefresh();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [projectId, debouncedRefresh, enabled]);

  return result;
}